import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { HistoryStore } from '@iotv/datamodel-core';
import { Badge, Button, FormControl, FormControlLabel, Grid, Stack, ThemeProvider, Tooltip } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { AppMainViewContext } from '../../../ContextProviders/AppContextProvider';
import { denseDataGridTheme } from '../../../cosmetics/Themes/DenseDataGrid';
import { AppLocalStorage } from '../../../data/LocalStorage/AppLocalStorage';
import { getServerInfo } from '../../../data/Wideawake/wideawakeDataFns';
import { useKeySelector } from '../../../hooks/useKeySelector';
import { useSelectedObjects } from '../../../hooks/useSelectedObjects';
import { getIconForType } from '../../../Iconography';
import { RangeableLineBarChart } from '../../../v2/components/charts/RangeableLineBarChart';
import DeviceSelection from '../../Device/DeviceSelection';
import { NavSections } from '../../navigation/NavSections';
import { getFlatLCDDeviceMessage } from './fns/getFlatLCDDeviceMessage';
import { DisplayMode, useNWDataTable } from './hooks/useNWDataTable';
import MessageData from '../../Device/tabs/NetworkAndMessages/MessageData';
const debug = process.env.REACT_APP_DEBUG && false;
const DeviceIcon = getIconForType('Device');
const graphableKeys = ['rssi', 'snr', 'sf', 'esp', 'fCntInc', 'battery'];
const graphableNames = {
    rssi: 'RSSI',
    snr: 'SNR',
    sf: 'Spreading Factor',
    esp: 'ESP',
    fCntInc: 'Frame Increment',
    battery: 'Battery',
};
/**
 * RSE — WYC, please don't remove this unless you have a better alternative.
 * When using NW Monitoring, it is key to have the list of devices saved,
 * to save searching for the same ones each time.
 */
const cachedSelectedDevices = AppLocalStorage.get(NavSections.NETWORKTABLEVIEW, 'selectedDevices');
const lcdMessagesToHistoryStore = (messages) => {
    const historyStore = new HistoryStore();
    for (const message of messages) {
        const flatLCDMessage = getFlatLCDDeviceMessage(message);
        const { timestamp, sf = null, fCntInc = null, rssi = null, snr = null, esp = null, battery = null } = flatLCDMessage;
        const stateSnapshot = {
            timestamp: timestamp,
            state: { rssi, snr, sf, esp, fCntInc, battery },
        };
        historyStore.push(stateSnapshot);
    }
    return historyStore;
};
export const NWDataTableView = ({}) => {
    const [{ fullscreen, isSmallView, contextCustomer, TransactionFunctions, UserFunctions }, dispatch] = useContext(AppMainViewContext);
    const [wideawakePresent, setWideawakePresent] = useState(false);
    const [deviceSelectorOpen, setDeviceSelectorOpen] = useState(false);
    useEffect(() => {
        getServerInfo().then(({ err, data }) => {
            if (err === null || data !== null)
                setWideawakePresent(true);
        });
    }, []);
    const { state: [[selectedDevices, _setSelectedDevices]], } = useSelectedObjects(cachedSelectedDevices, cachedSelectedDevices);
    const setSelectedDevices = (input) => {
        let next = [];
        if (typeof input === 'function') {
            next = input(selectedDevices);
        }
        else {
            _setSelectedDevices(input);
            next = input;
        }
        debug && console.log('setSelectedDevices', next);
        AppLocalStorage.set(NavSections.NETWORKTABLEVIEW, 'selectedDevices', next);
    };
    const { Table, LiveDataControl, SelectedOrAllControl, TimePeriodSelector, EveryNth, GoogleExport, ColumnVisibiltyControls, deviceMessages, displayMode } = useNWDataTable({
        selectedDevices,
    });
    // for chart of single device
    const filteredDeviceMessages = deviceMessages.filter((m) => m.deviceEui === selectedDevices[0]?.id);
    const historyObject = lcdMessagesToHistoryStore(filteredDeviceMessages);
    const { requiredKeys, component: KeySelector } = useKeySelector({
        initialKeys: graphableKeys,
        initialRequiredKeys: graphableKeys,
        readableKeyNames: graphableNames,
    });
    const bucketStrategyMap = {
    // fCntInc: BucketStrategy.APPORTION
    };
    // hide app drawer on this page
    useEffect(() => {
        const wasFullscreen = structuredClone(fullscreen);
        const newPartialState = { fullscreen: true, navDrawerOpen: false };
        dispatch(newPartialState);
        return () => {
            // don't un-fullscreen if we were already in fullscreen mode
            if (wasFullscreen)
                return;
            const newPartialState = { fullscreen: false, navDrawerOpen: false };
            dispatch(newPartialState);
        };
    }, []);
    return (_jsxs(Stack, { gap: 1, children: [_jsxs(Stack, { direction: 'row', gap: 1, children: [_jsx(Tooltip, { title: "Device Selection unavailable (Wideawake is not deployed)", disableInteractive: wideawakePresent, disableHoverListener: wideawakePresent, disableFocusListener: wideawakePresent, disableTouchListener: wideawakePresent, arrow: true, children: _jsx(Badge, { badgeContent: selectedDevices.length, color: selectedDevices.length === 0 ? 'error' : 'primary', showZero: true, children: _jsx(Button, { variant: 'outlined',
                                color: 'primary',
                                size: 'small',
                                disabled: !wideawakePresent,
                                onClick: () => !deviceSelectorOpen && setDeviceSelectorOpen(true),
                                endIcon: _jsx(DeviceIcon, {}), children: "Device Selection" }) }) }), _jsxs(Stack, { direction: "row", flexGrow: 1, justifyContent: 'center', gap: 1, children: [_jsx(FormControl, { children: LiveDataControl }), [DisplayMode.LIVE_DATA_ONLY, DisplayMode.LIVE_AND_QUERY].includes(displayMode) && (_jsx(FormControlLabel, { label: "Live Data:", labelPlacement: "start", control: SelectedOrAllControl }))] })] }), displayMode !== DisplayMode.LIVE_DATA_ONLY && (_jsxs(Stack, { direction: 'row', justifyContent: 'center', children: [TimePeriodSelector, EveryNth] })), _jsxs(Stack, { direction: 'row', alignItems: 'center', gap: 1, children: [ColumnVisibiltyControls, GoogleExport] }), selectedDevices.length === 1 && (_jsxs(Grid, { container: true, children: [_jsx(Grid, { size: { xs: 12, lg: 6 }, children: _jsx(MessageData, { device: selectedDevices[0],
                            lns: null,
                            mostProximateCustomer: contextCustomer,
                            transactionFunctions: TransactionFunctions,
                            userFunctions: UserFunctions }) }), _jsx(Grid, { size: { xs: 12, lg: 6 }, paddingInline: isSmallView ? 0 : 1, paddingBlockStart: isSmallView ? 1 : 0, children: _jsxs(Stack, { gap: 1, children: [_jsx(RangeableLineBarChart, { historyObject,
                                    bucketStrategyMap,
                                    timeSliceButtonValues: [],
                                    reverseSlicing: true,
                                    keys: requiredKeys,
                                    keyNameMapper: (k) => graphableNames[k] ?? k,
                                    fragmentProps: {
                                        style: { height: '30vh', maxHeight: 300 },
                                    },
                                    chartAreaProps: {
                                        top: 50,
                                        height: '100%',
                                        bottom: 50,
                                    } }), KeySelector] }) })] })), _jsx(ThemeProvider, { theme: denseDataGridTheme, children: Table }), _jsx(DeviceSelection, { visible: deviceSelectorOpen,
                close: () => setDeviceSelectorOpen(false),
                setSelectedDevices,
                selectedDevices })] }));
};
